import { createContext, ReactNode, useEffect, useReducer } from "react";

import { JWTContextType, ActionMap, AuthState, AuthUser } from "../types/auth";

import axiosInstance from "../utils/axios";
import { isValidToken, setSession, signOutAndRedirect } from "../utils/jwt";
import { baseUrl } from "../config";
import { useNavigate } from "react-router-dom";
import { SubscriptionStatus, SubscriptionSummary } from "../types/subscription";

const INITIALIZE = "INITIALIZE";
const SIGN_IN = "SIGN_IN";
const SIGN_OUT = "SIGN_OUT";
const SIGN_UP = "SIGN_UP";
const CHANGE_ASSOCIATION = "CHANGE_ASSOCIATION";
const SET_ASSOCIATION_SUBSCRIPTION = "SET_ASSOCIATION_SUBSCRIPTION";

type AuthActionTypes = {
  [INITIALIZE]: {
    isAuthenticated: boolean;
    currentUser: AuthUser;
  };
  [SIGN_IN]: {
    currentUser: AuthUser;
  };
  [SIGN_OUT]: undefined;
  [SIGN_UP]: {
    currentUser: AuthUser;
  };
  [CHANGE_ASSOCIATION]: { currentUser: AuthUser };
  [SET_ASSOCIATION_SUBSCRIPTION]: {
    associationSubscription: SubscriptionSummary | null;
  };
};

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  currentUser: null,
  associationSubscription: null,
};

const JWTReducer = (
  state: AuthState,
  action: ActionMap<AuthActionTypes>[keyof ActionMap<AuthActionTypes>]
) => {
  switch (action.type) {
    case INITIALIZE:
      // activeAssociation will be set by the initialize function with localStorage persistence
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        currentUser: action.payload.currentUser,
      };
    case SIGN_IN:
      return {
        ...state,
        isAuthenticated: true,
        currentUser: action.payload.currentUser,
      };
    case SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
        currentUser: null,
      };

    case SIGN_UP:
      return {
        ...state,
        isAuthenticated: true,
        currentUser: action.payload.currentUser,
      };
    case CHANGE_ASSOCIATION:
      return {
        ...state,
        currentUser: action.payload.currentUser,
      };
    case SET_ASSOCIATION_SUBSCRIPTION:
      return {
        ...state,
        associationSubscription: action.payload.associationSubscription,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);
  const navigate = useNavigate();

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          const { data } = await axiosInstance.get(`${baseUrl}/auth/verify`);
          const user: AuthUser = data.user;

          // Try to restore last selected association from localStorage
          const savedAssociationId = window.localStorage.getItem(
            "activeAssociationId"
          );
          let activeAssociation = user?.associations?.length
            ? user.associations[0]
            : null;

          // If we have a saved association ID, try to find it in user's associations
          if (savedAssociationId && user?.associations) {
            const savedAssoc = user.associations.find(
              (a: any) => a.id.toString() === savedAssociationId
            );
            if (savedAssoc) {
              activeAssociation = savedAssoc;
            }
          }

          dispatch({
            type: INITIALIZE,
            payload: {
              isAuthenticated: true,
              currentUser: { ...user, activeAssociation },
            },
          });

          await loadAssociationSubscription(activeAssociation?.id ?? null);
        } else {
          dispatch({
            type: INITIALIZE,
            payload: { isAuthenticated: false, currentUser: null },
          });
          dispatch({
            type: SET_ASSOCIATION_SUBSCRIPTION,
            payload: { associationSubscription: null },
          });
        }
      } catch {
        dispatch({
          type: INITIALIZE,
          payload: { isAuthenticated: false, currentUser: null },
        });
        dispatch({
          type: SET_ASSOCIATION_SUBSCRIPTION,
          payload: { associationSubscription: null },
        });
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data } = await axiosInstance.post(`${baseUrl}/auth/login`, {
      email,
      password,
    });
    const { accessToken, user } = data;
    setSession(accessToken);

    // Try to restore last selected association from localStorage
    const savedAssociationId = window.localStorage.getItem(
      "activeAssociationId"
    );
    let activeAssociation = user.associations?.[0] ?? null;

    // If we have a saved association ID, try to find it in user's associations
    if (savedAssociationId && user?.associations) {
      const savedAssoc = user.associations.find(
        (a: any) => a.id.toString() === savedAssociationId
      );
      if (savedAssoc) {
        activeAssociation = savedAssoc;
      }
    }

    dispatch({
      type: SIGN_IN,
      payload: { currentUser: { ...user, activeAssociation } },
    });
    await loadAssociationSubscription(activeAssociation?.id ?? null);
  };

  const refreshCurrentUser = async () => {
    const { data } = await axiosInstance.get(`${baseUrl}/auth/verify`);
    const user = data.user;

    // Try to restore last selected association from localStorage
    const savedAssociationId = window.localStorage.getItem(
      "activeAssociationId"
    );
    let activeAssociation = user.associations?.[0] ?? null;

    // If we have a saved association ID, try to find it in user's associations
    if (savedAssociationId && user?.associations) {
      const savedAssoc = user.associations.find(
        (a: any) => a.id.toString() === savedAssociationId
      );
      if (savedAssoc) {
        activeAssociation = savedAssoc;
      }
    }

    dispatch({
      type: SIGN_IN,
      payload: { currentUser: { ...user, activeAssociation } },
    });
    await loadAssociationSubscription(activeAssociation?.id ?? null);
  };

  const signOut = async () => {
    try {
      // Optional: Call backend to invalidate the token
      // await axiosInstance.post(`${baseUrl}/auth/logout`);

      // Clear saved association from localStorage
      window.localStorage.removeItem("activeAssociationId");

      // Clear session data locally
      signOutAndRedirect();

      // Update auth context state
      dispatch({ type: SIGN_OUT });
    } catch (error) {
      console.error("Logout error:", error);
      // Clear saved association even if server logout fails
      window.localStorage.removeItem("activeAssociationId");
      // Even if server logout fails, we should still clear local state
      signOutAndRedirect();
      dispatch({ type: SIGN_OUT });
    }
  };

  const signUp = async (data: any) => {
    try {
      await axiosInstance.post(`${baseUrl}/auth/register`, data);
    } catch (error: any) {
      throw error;
    }

    // window.localStorage.setItem("accessToken", accessToken);
    // dispatch({
    //   type: SIGN_UP,
    //   payload: {
    //     currentUser: user,
    //   },
    // });
  };

  const resetPassword = async (email: string) => {
    try {
      await axiosInstance.put(`${baseUrl}/users/create-new-password`, {
        email,
      });
    } catch (error: any) {
      console.error("Salasanan palauttaminen epäonnistui:", error);
      throw error;
    }
  };

  const changeAssociation = (newAssociation: any) => {
    if (state.currentUser) {
      const updatedUser = {
        ...state.currentUser,
        activeAssociation: newAssociation,
      };

      // Save selected association to localStorage for persistence
      if (newAssociation?.id) {
        window.localStorage.setItem(
          "activeAssociationId",
          newAssociation.id.toString()
        );
      }

      dispatch({
        type: CHANGE_ASSOCIATION,
        payload: { currentUser: updatedUser },
      });
      loadAssociationSubscription(newAssociation?.id ?? null);
      navigate("/");
    }
  };

  const loadAssociationSubscription = async (associationId?: number | null) => {
    if (!associationId) {
      dispatch({
        type: SET_ASSOCIATION_SUBSCRIPTION,
        payload: { associationSubscription: null },
      });
      return null;
    }
    try {
      const { data } = await axiosInstance.get<SubscriptionSummary>(
        `${baseUrl}/subscriptions/${associationId}/status`
      );
      dispatch({
        type: SET_ASSOCIATION_SUBSCRIPTION,
        payload: { associationSubscription: data },
      });
      return data;
    } catch {
      dispatch({
        type: SET_ASSOCIATION_SUBSCRIPTION,
        payload: { associationSubscription: null },
      });
      return null;
    }
  };

  const sub: SubscriptionSummary | null = state.associationSubscription ?? null;

  const now = Date.now();
  const status: SubscriptionStatus | null = sub?.status ?? null;
  const trialEndMs: number | null = sub?.trialEnd
    ? new Date(sub.trialEnd).getTime()
    : null;

  const trialActive: boolean =
    status === SubscriptionStatus.TRIALING &&
    trialEndMs !== null &&
    trialEndMs > now;

  const hasActivePaid: boolean = status === SubscriptionStatus.ACTIVE;

  const entitlements: Record<string, any> = sub?.entitlements ?? {};

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        signIn,
        signOut,
        signUp,
        resetPassword,
        changeAssociation,
        refreshCurrentUser,
        associationSubscription: sub,
        trialActive,
        hasActivePaid,
        entitlements,
        loadAssociationSubscription, // optional, if some screens want to force-refresh
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
